import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { auth, db, storage } from "./firebase";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  uploadString,
} from "firebase/storage";
import SignatureCanvas from "react-signature-canvas";
import heic2any from "heic2any";
import {
  UserPlus,
  LogOut,
  Phone,
  Sparkles,
  Search,
  User,
  ImageIcon,
  Calendar,
  Cake,
  Banknote,
  Users,
  BarChart3,
  LayoutList,
  ChevronDown,
  ChevronUp,
  Lock,
  Unlock,
  AlertCircle,
  Camera,
  Plus,
  Edit2,
} from "lucide-react";
import CustomerDetail from "./CustomerDetail";

const fetchAddr = async (zip) => {
  try {
    const response = await fetch(
      `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`,
    );
    const data = await response.json();
    if (data.results) {
      const res = data.results[0];
      return res.address1 + res.address2 + res.address3;
    }
    return "";
  } catch (e) {
    return "";
  }
};

function App() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsubscribe();
  }, []);
  if (!user) return <LoginScreen />;
  return (
    <Router>
      <div style={{ position: "relative", width: "100%", overflowX: "hidden" }}>
        <Routes>
          <Route path="/" element={<CustomerList user={user} />} />
          <Route path="/customer/:id" element={<CustomerDetail />} />
        </Routes>
      </div>
    </Router>
  );
}

function CustomerList({ user }) {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [sortType, setSortType] = useState("newest");
  const [showReport, setShowReport] = useState(false);
  const [showList, setShowList] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const sigPad = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // 編集モード管理
  const [editingCustomerId, setEditingCustomerId] = useState(null);

  const [name, setName] = useState("");
  const [furigana, setFurigana] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [address, setAddress] = useState("");
  const [birthday, setBirthday] = useState("");
  const [memo, setMemo] = useState("");
  const [masterConsentUrl, setMasterConsentUrl] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState("");

  const [stats, setStats] = useState({
    totalSales: 0,
    visitCount: 0,
    cancelCount: 0,
  });
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [targetMonth] = useState(
    `${new Date().getFullYear()}/${(new Date().getMonth() + 1).toString().padStart(2, "0")}`,
  );

  useEffect(() => {
    fetchCustomers();
    fetchMasterConsent();
  }, [user]);
  useEffect(() => {
    calculateStats();
  }, [customers]);

  const fetchCustomers = async () => {
    if (!user) return;
    const q = query(
      collection(db, "customers"),
      where("ownerId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    const querySnapshot = await getDocs(q);
    setCustomers(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const calculateStats = () => {
    const now = new Date();
    const thisMonthStr = `${now.getFullYear()}/${(now.getMonth() + 1).toString().padStart(2, "0")}`;
    let sales = 0;
    let visits = 0;
    let cancels = 0;
    const historyMap = {};
    customers.forEach((c) => {
      (c.visits || []).forEach((v) => {
        const vDate = new Date(v.date);
        const key = `${vDate.getFullYear()}/${(vDate.getMonth() + 1).toString().padStart(2, "0")}`;
        if (!historyMap[key])
          historyMap[key] = { sales: 0, visits: 0, cancels: 0, items: [] };
        historyMap[key].items.push({
          customerId: c.id,
          customerName: c.name,
          date: v.date,
          price: v.price || 0,
          isCancellation: v.isCancellation,
        });
        if (v.isCancellation) {
          historyMap[key].cancels++;
          if (key === thisMonthStr) cancels++;
        } else {
          historyMap[key].visits++;
          const p = Number(v.price || 0);
          historyMap[key].sales += p;
          if (key === thisMonthStr) {
            visits++;
            sales += p;
          }
        }
      });
    });
    setStats({ totalSales: sales, visitCount: visits, cancelCount: cancels });
    const sortedHistory = Object.keys(historyMap)
      .sort((a, b) => b.localeCompare(a))
      .map((key) => ({ month: key, ...historyMap[key] }));
    setMonthlyHistory(sortedHistory);
  };

  const fetchMasterConsent = async () => {
    const docRef = doc(db, "settings", user.uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) setMasterConsentUrl(docSnap.data().masterConsentUrl);
  };

  const handleMasterConsentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      let targetFile = file;
      if (file.name.toLowerCase().endsWith(".heic")) {
        const blob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.7,
        });
        targetFile = new File([blob], "master_consent.jpg", {
          type: "image/jpeg",
        });
      }
      const sRef = ref(
        storage,
        `settings/${user.uid}/master_consent_${Date.now()}.jpg`,
      );
      await uploadBytes(sRef, targetFile);
      const url = await getDownloadURL(sRef);
      await setDoc(
        doc(db, "settings", user.uid),
        { masterConsentUrl: url, ownerId: user.uid },
        { merge: true },
      );
      setMasterConsentUrl(url);
      alert("原本を更新しました");
    } catch (e) {
      alert("エラー: " + e.message);
    }
  };

  // 顧客の編集開始
  const handleEditProfile = (c) => {
    setEditingCustomerId(c.id);
    setName(c.name);
    setFurigana(c.furigana || "");
    setPhone(c.phone || "");
    setZip(c.zip || "");
    setAddress(c.address || "");
    setBirthday(c.birthday || "");
    setMemo(c.memo || "");
    setCurrentProfileImageUrl(c.profileImage || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let signatureUrl = "";
    let profileImageUrl = currentProfileImageUrl;
    try {
      if (sigPad.current && !sigPad.current.isEmpty()) {
        const sData = sigPad.current.getCanvas().toDataURL("image/png");
        const sRef = ref(
          storage,
          `signatures/${user.uid}/${Date.now()}_sig.png`,
        );
        await uploadString(sRef, sData, "data_url");
        signatureUrl = await getDownloadURL(sRef);
      }
      if (profileImage) {
        const pRef = ref(
          storage,
          `profiles/${user.uid}/${Date.now()}_icon.jpg`,
        );
        await uploadBytes(pRef, profileImage);
        profileImageUrl = await getDownloadURL(pRef);
      }

      const data = {
        name,
        furigana,
        phone,
        zip,
        address,
        birthday,
        memo,
        profileImage: profileImageUrl,
      };

      if (editingCustomerId) {
        if (signatureUrl) data.signatureImage = signatureUrl;
        await updateDoc(doc(db, "customers", editingCustomerId), data);
        alert("更新しました");
      } else {
        await addDoc(collection(db, "customers"), {
          ...data,
          ownerId: user.uid,
          consentImage: masterConsentUrl,
          signatureImage: signatureUrl || "",
          visits: [],
          reservations: [],
          createdAt: serverTimestamp(),
        });
        alert("登録しました");
      }

      // リセット
      setName("");
      setFurigana("");
      setPhone("");
      setZip("");
      setAddress("");
      setBirthday("");
      setMemo("");
      setProfileImage(null);
      setCurrentProfileImageUrl("");
      setEditingCustomerId(null);
      setIsSigning(false);
      sigPad.current.clear();
      fetchCustomers();
    } catch (e) {
      alert("エラー: " + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLastVisitDate = (visits) => {
    const actual = (visits || []).filter((v) => !v.isCancellation);
    if (actual.length === 0) return "0000-00-00";
    return [...actual].sort((a, b) => new Date(b.date) - new Date(a.date))[0]
      .date;
  };

  const getNextResDate = (res) => {
    const future = (res || [])
      .filter((r) => new Date(r.start) >= new Date())
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    return future.length > 0 ? future[0].start : null;
  };

  const isBirthdayNear = (b) => {
    if (!b) return false;
    const now = new Date();
    const bday = new Date(b);
    const thisYearBday = new Date(
      now.getFullYear(),
      bday.getMonth(),
      bday.getDate(),
    );
    const diff = Math.ceil((thisYearBday - now) / (1000 * 60 * 60 * 24));
    return diff >= 0 && diff <= 14;
  };

  const displayCustomers = customers
    .filter((c) =>
      (c.name + (c.furigana || ""))
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )
    .filter((c) => {
      if (filterPeriod === "all") return true;
      const last = getLastVisitDate(c.visits);
      if (filterPeriod === "1m")
        return (
          last !== "0000-00-00" &&
          Math.ceil((new Date() - new Date(last)) / (1000 * 60 * 60 * 24)) >= 30
        );
      if (filterPeriod === "none") return last === "0000-00-00";
      return true;
    })
    .sort((a, b) => {
      if (sortType === "alphabetical")
        return (a.furigana || a.name).localeCompare(b.furigana || b.name, "ja");
      if (sortType === "birthday") {
        const getB = (s) =>
          s ? new Date(s).getMonth() * 100 + new Date(s).getDate() : 9999;
        return getB(a.birthday) - getB(b.birthday);
      }
      if (sortType === "lastVisit")
        return getLastVisitDate(b.visits).localeCompare(
          getLastVisitDate(a.visits),
        );
      if (sortType === "visitCount")
        return (
          (b.visits || []).filter((v) => !v.isCancellation).length -
          (a.visits || []).filter((v) => !v.isCancellation).length
        );
      if (sortType === "nextRes")
        return (getNextResDate(a.reservations) || "9999-99-99").localeCompare(
          getNextResDate(b.reservations) || "9999-99-99",
        );
      if (sortType === "cancel")
        return (
          (b.visits || []).filter((v) => v.isCancellation).length -
          (a.visits || []).filter((v) => v.isCancellation).length
        );
      return 0; // newest
    });

  return (
    <div style={styles.mainContainer}>
      <header style={styles.header}>
        <div style={styles.headerLogo}>
          <Sparkles color="#0ea5e9" size={20} />{" "}
          <span style={{ fontSize: "20px" }}>Salon Karte</span>
        </div>
        <div style={styles.headerActions}>
          <label style={styles.masterConsentBtn}>
            <ImageIcon size={18} /> <span>原本登録</span>
            <input
              type="file"
              accept="image/*,.heic"
              onChange={handleMasterConsentUpload}
              style={{ display: "none" }}
            />
          </label>
          <button
            onClick={() => setShowReport(!showReport)}
            style={styles.toggleBtn}
          >
            {showReport ? <LayoutList size={20} /> : <BarChart3 size={20} />}
          </button>
          <button onClick={() => signOut(auth)} style={styles.logoutBtn}>
            <LogOut size={20} />
          </button>
        </div>
      </header>
      <main style={styles.content}>
        {showReport ? (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <BarChart3 size={24} /> 売上分析
            </h2>
            <div style={styles.dashboardGrid}>
              <div style={styles.statCard}>
                <div style={styles.statIconBox}>
                  <Banknote color="#10b981" />
                </div>
                <div>
                  <div style={styles.statLabel}>今月の売上</div>
                  <div style={styles.statValue}>
                    ¥{stats.totalSales.toLocaleString()}
                  </div>
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statIconBox}>
                  <Users color="#0ea5e9" />
                </div>
                <div>
                  <div style={styles.statLabel}>今月の来店</div>
                  <div style={styles.statValue}>{stats.visitCount}回</div>
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statIconBox}>
                  <AlertCircle color="#ef4444" />
                </div>
                <div>
                  <div style={styles.statLabel}>キャンセル</div>
                  <div style={styles.statValue}>{stats.cancelCount}回</div>
                </div>
              </div>
            </div>
            <div style={styles.chartWrapper}>
              <h3 style={styles.chartTitle}>月別売上推移</h3>
              <div style={styles.barChartContainer}>
                {monthlyHistory
                  .slice(0, 6)
                  .reverse()
                  .map((data, i) => {
                    const maxSales =
                      Math.max(...monthlyHistory.map((m) => m.sales)) || 1;
                    return (
                      <div key={i} style={styles.barGroup}>
                        <div style={styles.barValue}>
                          ¥{Math.floor(data.sales / 1000)}k
                        </div>
                        <div
                          style={{
                            ...styles.bar,
                            height: `${(data.sales / maxSales) * 100}%`,
                          }}
                        />
                        <div style={styles.barLabel}>
                          {data.month.split("/")[1]}月
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div style={styles.tableCard}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>日付</th>
                    <th style={styles.th}>お名前</th>
                    <th style={styles.th}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    monthlyHistory.find((h) => h.month === targetMonth)
                      ?.items || []
                  )
                    .filter((item) => !item.isCancellation)
                    .map((item, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>
                          {item.date.split("-")[1]}/{item.date.split("-")[2]}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            color: "#0ea5e9",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                          onClick={() =>
                            navigate(`/customer/${item.customerId}`)
                          }
                        >
                          {item.customerName} 様
                        </td>
                        <td style={styles.td}>
                          ¥{item.price.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <>
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <UserPlus size={20} />{" "}
                {editingCustomerId ? "顧客情報の編集" : "顧客登録"}
              </h2>
              <form onSubmit={handleSubmit} style={styles.form}>
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <label style={styles.profileUploadLabel}>
                    {profileImage ? (
                      <img
                        src={URL.createObjectURL(profileImage)}
                        style={styles.profilePreview}
                        alt="p"
                      />
                    ) : currentProfileImageUrl ? (
                      <img
                        src={currentProfileImageUrl}
                        style={styles.profilePreview}
                        alt="p"
                      />
                    ) : (
                      <Camera size={24} color="#94a3b8" />
                    )}
                    <input
                      type="file"
                      accept="image/*,.heic"
                      onChange={(e) => setProfileImage(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                    <div style={styles.profilePlus}>
                      <Plus size={12} />
                    </div>
                  </label>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={styles.inputField}
                      placeholder="お名前"
                      required
                    />
                    <input
                      value={furigana}
                      onChange={(e) => setFurigana(e.target.value)}
                      style={styles.inputField}
                      placeholder="ふりがな"
                    />
                  </div>
                </div>
                <div style={styles.inputGrid}>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={styles.inputField}
                    placeholder="電話番号"
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <label style={{ fontSize: "10px", color: "#64748b" }}>
                      生年月日
                    </label>
                    <input
                      type="date"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                      style={styles.inputField}
                    />
                  </div>
                </div>
                <div style={styles.inputGrid}>
                  <input
                    value={zip}
                    onChange={(e) => {
                      setZip(e.target.value);
                      if (e.target.value.length === 7)
                        fetchAddr(e.target.value).then((a) => setAddress(a));
                    }}
                    style={styles.inputField}
                    placeholder="郵便番号"
                  />
                  <input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    style={styles.inputField}
                    placeholder="住所"
                  />
                </div>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  style={styles.textarea}
                  placeholder="メモ..."
                />
                <div style={styles.consentBox}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <p style={{ fontSize: "12px", color: "#64748b" }}>
                      ▼ サイン{editingCustomerId && " (変更する場合のみ記入)"}
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsSigning(!isSigning)}
                      style={{
                        ...styles.signToggleBtn,
                        backgroundColor: isSigning ? "#f1f5f9" : "#0ea5e9",
                        color: isSigning ? "#64748b" : "#fff",
                      }}
                    >
                      {isSigning ? (
                        <>
                          <Lock size={12} /> ロック
                        </>
                      ) : (
                        <>
                          <Unlock size={12} /> サイン記入
                        </>
                      )}
                    </button>
                  </div>
                  <div
                    style={{
                      ...styles.sigWrapper,
                      pointerEvents: isSigning ? "auto" : "none",
                      opacity: isSigning ? 1 : 0.6,
                    }}
                  >
                    <SignatureCanvas
                      ref={sigPad}
                      penColor="black"
                      canvasProps={{ style: styles.sigCanvas }}
                    />
                  </div>
                  {isSigning && (
                    <button
                      type="button"
                      onClick={() => sigPad.current.clear()}
                      style={styles.clearBtn}
                    >
                      消去
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ ...styles.submitBtn, flex: 2 }}
                  >
                    {isSubmitting
                      ? "保存中..."
                      : editingCustomerId
                        ? "情報を更新する"
                        : "同意して登録する"}
                  </button>
                  {editingCustomerId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCustomerId(null);
                        setName("");
                        setFurigana("");
                        setPhone("");
                        setZip("");
                        setAddress("");
                        setBirthday("");
                        setMemo("");
                        setProfileImage(null);
                        setCurrentProfileImageUrl("");
                        sigPad.current.clear();
                      }}
                      style={{
                        ...styles.submitBtn,
                        backgroundColor: "#94a3b8",
                        flex: 1,
                      }}
                    >
                      キャンセル
                    </button>
                  )}
                </div>
              </form>
            </section>
            <div style={{ marginTop: "20px" }}>
              <button
                onClick={() => setShowList(!showList)}
                style={styles.listToggleBtn}
              >
                {showList ? (
                  <>
                    <ChevronUp size={18} /> 閉じる
                  </>
                ) : (
                  <>
                    <ChevronDown size={18} /> 顧客リストを表示
                  </>
                )}
              </button>
              {showList && (
                <section style={{ ...styles.section, marginTop: "15px" }}>
                  <div style={styles.listHeader}>
                    <div style={styles.searchBox}>
                      <Search size={18} color="#94a3b8" />
                      <input
                        placeholder="名前検索..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInner}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        flexWrap: "wrap",
                        marginTop: "10px",
                      }}
                    >
                      <select
                        value={filterPeriod}
                        onChange={(e) => setFilterPeriod(e.target.value)}
                        style={styles.select}
                      >
                        <option value="all">全期間</option>
                        <option value="1m">1ヶ月無</option>
                        <option value="none">来店無</option>
                      </select>
                      <select
                        value={sortType}
                        onChange={(e) => setSortType(e.target.value)}
                        style={styles.select}
                      >
                        <option value="newest">新着順</option>
                        <option value="lastVisit">来店日時順</option>
                        <option value="visitCount">来店数順</option>
                        <option value="birthday">誕生日順</option>
                        <option value="nextRes">次回予約順</option>
                        <option value="cancel">キャンセル順</option>
                        <option value="alphabetical">五十音</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.customerGrid}>
                    {displayCustomers.map((c) => {
                      const nextR = getNextResDate(c.reservations);
                      const vCount = (c.visits || []).filter(
                        (v) => !v.isCancellation,
                      ).length;
                      const cCount = (c.visits || []).filter(
                        (v) => v.isCancellation,
                      ).length;
                      return (
                        <div key={c.id} style={styles.customerCard}>
                          <div style={styles.cardHeader}>
                            <div style={styles.avatarWrap}>
                              {c.profileImage ? (
                                <img
                                  src={c.profileImage}
                                  style={styles.avatarImg}
                                  alt="p"
                                />
                              ) : (
                                <User color="#94a3b8" />
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <h3 style={styles.cardName}>{c.name} 様</h3>
                              <p style={styles.cardSub}>
                                来店:{vCount} / キャンセル:{cCount}
                              </p>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "5px",
                              marginBottom: "10px",
                              flexWrap: "wrap",
                            }}
                          >
                            {isBirthdayNear(c.birthday) && (
                              <span style={styles.bdayBadge}>
                                <Cake size={10} /> 誕生日近
                              </span>
                            )}
                            {nextR && (
                              <span style={styles.resBadge}>
                                <Calendar size={10} /> 次回:
                                {nextR.replace("T", " ").slice(5)}
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={() => navigate(`/customer/${c.id}`)}
                              style={{ ...styles.cardBtn, flex: 1 }}
                            >
                              詳細
                            </button>
                            <button
                              onClick={() => handleEditProfile(c)}
                              style={{
                                ...styles.cardBtn,
                                flex: 1,
                                backgroundColor: "#f1f5f9",
                                color: "#64748b",
                              }}
                            >
                              <Edit2 size={14} /> 編集
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginCard}>
        <Sparkles color="#0ea5e9" size={40} />
        <h1>Salon Karte</h1>
        <input
          type="email"
          placeholder="メール"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.inputField}
        />
        <input
          type="password"
          placeholder="パスワード"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.inputField}
        />
        <button
          onClick={() => signInWithEmailAndPassword(auth, email, password)}
          style={styles.submitBtn}
        >
          ログイン
        </button>
      </div>
    </div>
  );
}

const styles = {
  mainContainer: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
  },
  header: {
    backgroundColor: "#fff",
    padding: "10px 15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  headerLogo: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontWeight: "bold",
  },
  headerActions: { display: "flex", gap: "8px", alignItems: "center" },
  masterConsentBtn: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    backgroundColor: "#f1f5f9",
    padding: "6px 10px",
    borderRadius: "8px",
    fontSize: "11px",
    color: "#0ea5e9",
    cursor: "pointer",
    border: "1px dashed #0ea5e9",
  },
  toggleBtn: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    padding: "8px",
    borderRadius: "10px",
  },
  logoutBtn: {
    border: "none",
    background: "none",
    color: "#64748b",
    cursor: "pointer",
  },
  content: {
    padding: "15px",
    maxWidth: "1200px",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  section: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "20px",
    border: "1px solid #e2e8f0",
    marginBottom: "20px",
    boxSizing: "border-box",
  },
  sectionTitle: {
    fontSize: "18px",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  form: { display: "flex", flexDirection: "column", gap: "10px" },
  profileUploadLabel: {
    width: "80px",
    height: "80px",
    borderRadius: "20px",
    backgroundColor: "#f1f5f9",
    border: "2px dashed #e2e8f0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
  },
  profilePreview: { width: "100%", height: "100%", objectFit: "cover" },
  profilePlus: {
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundColor: "#0ea5e9",
    color: "#fff",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  inputGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden", // はみ出しを防止
  },
  inputField: {
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0, // Flex/Grid内での突き抜けを防止
    backgroundColor: "#fff", // 背景を白に固定
    WebkitAppearance: "none", // iPhone特有のスタイルをリセット
  },
  textarea: {
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    minHeight: "80px",
    width: "100%",
    boxSizing: "border-box",
  },
  submitBtn: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    border: "none",
    padding: "15px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "16px",
    cursor: "pointer",
  },
  consentBox: {
    backgroundColor: "#f1f5f9",
    padding: "15px",
    borderRadius: "15px",
    border: "1px solid #e2e8f0",
  },
  signToggleBtn: {
    border: "none",
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "11px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
  },
  sigWrapper: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  sigCanvas: { width: "100%", height: "100px" },
  clearBtn: {
    border: "none",
    background: "none",
    color: "#ef4444",
    fontSize: "11px",
    marginTop: "5px",
  },
  listToggleBtn: {
    width: "100%",
    padding: "15px",
    border: "none",
    background: "#e2e8f0",
    color: "#475569",
    borderRadius: "15px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  listHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "20px",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#fff",
    padding: "10px 15px",
    borderRadius: "15px",
    border: "1px solid #e2e8f0",
  },
  searchInner: {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: "15px",
  },
  select: {
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "13px",
    backgroundColor: "#fff",
  },
  customerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "15px",
  },
  customerCard: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    boxSizing: "border-box",
  },
  cardHeader: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    marginBottom: "10px",
  },
  avatarWrap: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    backgroundColor: "#f1f5f9",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  cardName: { margin: 0, fontSize: "17px", fontWeight: "bold" },
  cardSub: { margin: 0, fontSize: "12px", color: "#64748b" },
  badge: {
    fontSize: "10px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "5px",
    backgroundColor: "#f1f5f9",
  },
  bdayBadge: {
    fontSize: "10px",
    color: "#ef4444",
    backgroundColor: "#fef2f2",
    padding: "2px 8px",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },
  resBadge: {
    fontSize: "10px",
    color: "#0ea5e9",
    backgroundColor: "#f0f9ff",
    padding: "2px 8px",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },
  cardBtn: {
    backgroundColor: "#f1f5f9",
    border: "none",
    padding: "10px",
    borderRadius: "10px",
    fontWeight: "bold",
    color: "#0ea5e9",
    cursor: "pointer",
  },
  dashboardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
  },
  statCard: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "24px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    border: "1px solid #e2e8f0",
  },
  statIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "16px",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  statLabel: { fontSize: "12px", color: "#64748b", fontWeight: "bold" },
  statValue: { fontSize: "20px", fontWeight: "900" },
  chartWrapper: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "24px",
    border: "1px solid #e2e8f0",
    marginBottom: "25px",
  },
  barChartContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-around",
    height: "120px",
    paddingBottom: "10px",
    borderBottom: "2px solid #f1f5f9",
  },
  barGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "12%",
    height: "100%",
    justifyContent: "flex-end",
    gap: "5px",
  },
  bar: {
    width: "80%",
    backgroundColor: "#0ea5e9",
    borderRadius: "6px 6px 0 0",
    minHeight: "2px",
  },
  barLabel: { fontSize: "10px", fontWeight: "bold", color: "#64748b" },
  barValue: { fontSize: "9px", color: "#94a3b8" },
  tableCard: {
    backgroundColor: "#fff",
    borderRadius: "24px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "15px",
    fontSize: "12px",
    color: "#64748b",
    borderBottom: "1px solid #f1f5f9",
    textAlign: "left",
  },
  td: {
    padding: "12px 15px",
    fontSize: "14px",
    borderBottom: "1px solid #f1f5f9",
  },
  loginContainer: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f1f5f9",
  },
  loginCard: {
    backgroundColor: "#fff",
    padding: "40px",
    borderRadius: "30px",
    textAlign: "center",
    width: "90%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
};

export default App;
